import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { IListResponseData, TrainingType } from '@pro-connect/shared';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { ProEducationService } from './pro-education.service';
import { ITrainingsListDto } from 'shared/src/pages/core/models/trainings-list-dto';
import { ITrainingsList } from 'shared/src/pages/core/models/trainings-list';

export class ProEducationStateGetCategories {
  public static readonly type = '[ProEducationState] get categories';
}

export class ProEducationStateGetPlaces {
  public static readonly type = '[ProEducationState] get places';
}

export class ProEducationStateGetTrainings {
  public static readonly type = '[ProEducationState] get trainings';
}

export class ProEducationStateFilterChange {
  public static readonly type = '[ProEducationState] filter change';

  constructor(public payload: { listDto: ITrainingsListDto }) {}
}

export interface ProEducationStateModel {
  loading: boolean;
  listDto: ITrainingsListDto;
  list: IListResponseData<ITrainingsList> | null;
  categories: { id: number; name: string }[];
  places: { id: number; name: string }[];
}

@State<ProEducationStateModel>({
  name: 'proEducation',
  defaults: {
    loading: false,
    listDto: {
      perPage: 6,
      page: 1,
      direction: 'desc',
      sort: 'id',
      training_type: TrainingType.TRAINING,
      target: 'none',
    },
    list: null,
    categories: [],
    places: [],
  },
})
@Injectable()
export class ProEducationState {
  public constructor(private readonly _proEducationService: ProEducationService) {}

  @Selector()
  public static loading({ loading }: ProEducationStateModel): boolean {
    return loading;
  }

  @Selector()
  public static list({ list }: ProEducationStateModel): IListResponseData<ITrainingsList> | null {
    return list;
  }

  @Selector()
  public static categories({ categories }: ProEducationStateModel): { id: number; name: string }[] {
    return categories;
  }

  @Selector()
  public static places({ places }: ProEducationStateModel): { id: number; name: string }[] {
    return places;
  }

  @Action(ProEducationStateGetTrainings)
  public getTrainings(state: StateContext<ProEducationStateModel>): Observable<IListResponseData<ITrainingsList>> {
    const { getState, patchState } = state;

    const { listDto } = getState();

    patchState({
      loading: true,
    });

    return this._proEducationService.getTrainings(listDto).pipe(
      tap((data: IListResponseData<ITrainingsList>) => {
        patchState({
          list: {
            ...data,
            data: [
              ...data.data.map((el: ITrainingsList) => {
                return { ...el, price: Math.round(el.price) };
              }),
            ],
          },
          listDto,
        });
      }),
      finalize(() => {
        patchState({
          loading: false,
        });
      }),
    );
  }

  @Action(ProEducationStateGetCategories)
  public getCategories({
    patchState,
    getState,
  }: StateContext<ProEducationStateModel>): Observable<{ id: number; name: string }[]> {
    patchState({
      loading: true,
    });

    return this._proEducationService.getTrainingsCategories().pipe(
      tap((categories: { id: number; name: string }[]) => {
        patchState({
          categories: [...categories],
        });
      }),
      finalize(() => {
        patchState({
          loading: false,
        });
      }),
    );
  }

  @Action(ProEducationStateGetPlaces)
  public getPlaces(state: StateContext<ProEducationStateModel>): Observable<{ id: number; name: string }[]> {
    const { patchState } = state;

    patchState({
      loading: true,
    });

    return this._proEducationService.getTrainingsPlaces().pipe(
      tap((places: { id: number; name: string }[]) => {
        patchState({
          places: [...places],
        });
      }),
      finalize(() => {
        patchState({
          loading: false,
        });
      }),
    );
  }

  @Action(ProEducationStateFilterChange)
  public filterChange(
    state: StateContext<ProEducationStateModel>,
    { payload: { listDto } }: ProEducationStateFilterChange,
  ): void {
    const { patchState, dispatch } = state;
    patchState({
      listDto: { ...listDto },
    });

    dispatch([new ProEducationStateGetTrainings()]);
  }
}
